import type { UserData, ValidateTokenResponse } from '@/login/types/AccessTokenData';
import { StorageSerializers, useLocalStorage, type RemovableRef } from '@vueuse/core';
import { defineStore } from 'pinia';
import type { Model } from '@/login/composables/useResgate';
import { InstanceAccountType } from '@sysarb/sysarb-types/service-base/enums';
import type { SsoSettingsByUsernameResult } from '@/api/login';
import { LoginType } from '@sysarb/sysarb-types/services/v1/sso';

type LoginStoreData = {
    _tokenUserUserInfo: ValidateTokenResponse | null;
    _instance: RemovableRef<string | null>;
    _userData: Model<UserData> | null;
    _resgateAuthenticated: boolean;
    _savedUsername: RemovableRef<string | null>;
    _saveUsername: RemovableRef<boolean>;
    _forgotPassword: boolean;
    _loggingIn: boolean;
    _instancesLoading: number;
    _passwordLoginError: boolean;
    _smsLoginError: boolean;
    _requireSms: boolean;
    _lastAuthenticatedInstance: string | null;
    _showInstanceChangedWarning: boolean;
    _loginFlowData: Exclude<SsoSettingsByUsernameResult, null>;
};

export const useLoginStore = defineStore('loginStore', {
    state: (): LoginStoreData => ({
        _tokenUserUserInfo: null,
        _instance: useLocalStorage<string | null>('__instance', null, { serializer: StorageSerializers.string }),
        _userData: null,
        _resgateAuthenticated: false,
        _savedUsername: useLocalStorage<string | null>('__savedUsername', null, { serializer: StorageSerializers.string, flush: 'sync' }),
        _saveUsername: useLocalStorage<boolean>('__saveUsername', false, { serializer: StorageSerializers.boolean, flush: 'sync' }),
        _forgotPassword: false,
        _loggingIn: false,
        _instancesLoading: 0,
        _passwordLoginError: false,
        _smsLoginError: false,
        _requireSms: false,
        _lastAuthenticatedInstance: null,
        _showInstanceChangedWarning: false,
        _loginFlowData: {
            username: '',
            loginType: LoginType.Password,
            sso: [],
            password: null
        },
    }),
    getters: {
        /** Working user id, should not be used except for login purposes. */
        tokenUserInfo: state => state._tokenUserUserInfo,
        /**
         * User id
         */
        userId: state => state._userData?.loginUser?.Id ?? null,
        /**
         * Instance (subdomain).
         */
        instance: state => state._instance,
        /**
         * Instance prepared for use in resgate topics.
         */
        instanceTopic: state => (state._instance == null ? null : state._instance.replace(/\./g, '_')),
        /**
         * Access token data (contains all user and instance data).
         */
        userData: state => state._userData,
        /**
         * User is logged in, resgate is authenticated and required data has been loaded.
         */
        isLoggedIn: state => {
            if (state._userData != null) return true;
            return false;
        },
        /**
         * An instance has been selected.
         */
        isInstanceSelected: state => state._instance != null,
        /**
         * Resgate is authenticated.
         */
        resgateAuthenticated: state => state._resgateAuthenticated,
        /**
         * Current instance.
         */
        selectedInstance: state => {
            if (state._userData == null) return null;
            if (state._userData.calculatedInstances == null) return null;
            if (state._instance == null) return null;

            return Array.from(state._userData.calculatedInstances ?? []).find(i => i.SubDomain === state._instance) ?? null;
        },
        jobArchitectureSettings: state => {
            if (state._userData == null) return null;
            if (state._userData.calculatedInstances == null) return null;
            if (state._instance == null) return null;

            const instance = Array.from(state._userData.calculatedInstances ?? []).find(i => i.SubDomain === state._instance);
            if (instance == null) return null;

            return instance.jobArchitectureSettings ?? null;
        },
        /**
         * Username saved in local storage.
         */
        savedUsername: state => state._savedUsername,
        /**
         * Save username in local storage.
         */
        saveUsername: state => state._saveUsername,
        /**
         * Currently active role.
         */
        role: state => {
            if (state._userData == null) return null;
            if (state._userData.calculatedInstances == null) return null;
            if (state._instance == null) return null;

            const instance = Array.from(state._userData.calculatedInstances ?? []).find(i => i.SubDomain === state._instance);
            if (instance == null) return null;

            return instance.user?.roles?.find(c => c.isActive === true) ?? null;
        },
        roles: state => {
            if (state._userData == null) return null;
            if (state._userData.calculatedInstances == null) return null;
            if (state._instance == null) return null;

            const instance = Array.from(state._userData.calculatedInstances ?? []).find(i => i.SubDomain === state._instance);
            if (instance == null) return null;

            return instance.user?.roles;
        },
        /**
         * Currently active instance account type.
         */
        instanceAccountType: state => {
            if (state._userData == null) return null;
            if (state._userData.calculatedInstances == null) return null;
            if (state._instance == null) return null;

            return state._userData.loginUser?.accountType ?? null;
        },
        loggingIn: state => state._loggingIn,
        instancesLoading: state => state._instancesLoading,
        tokenIsAuthenticated: state => {
            if (state._userData == null) return false;
            return false;
        },
        instanceCount: state => {
            if (state._userData == null) return 0;
            return Array.from(state._userData.calculatedInstances ?? []).length;
        },
        currentOrganisationNodeId: state => {
            if (state._userData == null) return null;
            if (state._userData.calculatedInstances == null) return null;
            if (state._instance == null) return null;

            const instance = Array.from(state._userData.calculatedInstances ?? []).find(i => i.SubDomain === state._instance);
            if (instance == null) return null;

            return instance.user?.roles?.find(c => c.isActive === true)?.organisationNodeId ?? null;
        },
        /** Is forgot password toggled */
        forgotPassword: state => state._forgotPassword,
        employee: state => {
            if (state._userData == null) return null;
            if (state._userData.calculatedInstances == null) return null;
            if (state._instance == null) return null;

            const instance = Array.from(state._userData.calculatedInstances ?? []).find(i => i.SubDomain === state._instance);
            if (instance == null) return null;

            return instance.user?.employee ?? null;
        },
        passwordLoginError: state => state._passwordLoginError,
        smsLoginError: state => state._smsLoginError,
        /** The current password login requires an SMS code before it completes. */
        requireSms: state => state._requireSms,
        emailVerified: state => state._userData?.loginUser?.VerifiedEmail ?? false,
        lastAuthenticatedInstance: state => state._lastAuthenticatedInstance,
        instanceUserId: state => {
            if (state._userData == null) return null;
            if (state._userData.calculatedInstances == null) return null;
            if (state._instance == null) return null;

            const instance = Array.from(state._userData.calculatedInstances ?? []).find(i => i.SubDomain === state._instance) ?? null;

            if (instance == null) return null;
            return instance.user?.ID ?? null;
        },
        isSupport: state => state._userData?.loginUser?.accountType === InstanceAccountType.Support,
        showInstanceChangedWarning: state => state._showInstanceChangedWarning,
        isSimulating: state => {
            if (state._userData == null) return false;
            return state._tokenUserUserInfo?.isSimulated === true;
        },
        allowedToCreateUsers: state => {
            if (state._userData == null) return false;
            if (state._userData.calculatedInstances == null) return false;
            if (state._instance == null) return false;

            // Always allow support.
            if (state._userData.loginUser?.accountType === InstanceAccountType.Support) return true;

            const selectedInstance = Array.from(state._userData.calculatedInstances ?? []).find(i => i.SubDomain === state._instance) ?? null;
            if (selectedInstance?.basics == null) return false;

            // Do not allow on template instances.
            return selectedInstance.basics.isTemplate !== true;
        },
        passwordRequirements: state => {
            if (state._userData == null) return null;
            return state._userData.passwordrequirements ?? null;
        },
        isDev: () => import.meta.env.DEV === true,
        loginFlowData: state => state._loginFlowData,
    },
    actions: {
        setTokenUserInfo(val: ValidateTokenResponse | null) {
            this._tokenUserUserInfo = val;
        },
        setInstance(val: string | null) {
            this._instance = val;
        },
        setUserData(val: Model<UserData> | null) {
            this._userData = val;
        },
        setResgateAuthenticated(val: boolean) {
            this._resgateAuthenticated = val;
        },
        setSavedUsername(val: string | null) {
            this._savedUsername = val;
        },
        setSaveUsername(val: boolean) {
            this._saveUsername = val;

            if (val === false) {
                this.setSavedUsername(null);
            }
        },
        setLoggingIn(val: boolean) {
            this._loggingIn = val;
        },
        setInstancesLoading(val: number) {
            this._instancesLoading = val;
        },
        setForgotPassword(val: boolean) {
            this._forgotPassword = val;
        },
        setPasswordLoginError(val: boolean) {
            this._passwordLoginError = val;
        },
        setSmsLoginError(val: boolean) {
            this._smsLoginError = val;
        },
        setRequireSms(val: boolean) {
            this._requireSms = val;
        },
        clear() {
            this.setUserData(null);
            this.setResgateAuthenticated(false);
            this.setLoggingIn(false);
            this.setRequireSms(false);
            this.setInstancesLoading(0);
            this.setInstanceChangedWarning(false);
            this.setLoginFlowData(null);
        },
        setLastAuthenticatedInstance(val: string | null) {
            this._lastAuthenticatedInstance = val;
        },
        setInstanceChangedWarning(val: boolean) {
            this._showInstanceChangedWarning = val;
        },
        setLoginFlowData(val: SsoSettingsByUsernameResult) {
            if (val == null) {
                this._loginFlowData = {
                    username: '',
                    loginType: LoginType.Password,
                    sso: [],
                    password: null
                };
                return;
            }
            this._loginFlowData = val;
        }
    }
});
